import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";

interface Party {
  role: string;
  name: string;
  email: string;
}

interface DeployWorkflowAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DeployWorkflowAgentDialog({ open, onOpenChange, onSuccess }: DeployWorkflowAgentDialogProps) {
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [name, setName] = useState("");
  const [emailAlias, setEmailAlias] = useState("");
  const [parties, setParties] = useState<Party[]>([
    { role: "fund_admin", name: "", email: "" },
    { role: "banking_rm", name: "", email: "" },
    { role: "fo_rm", name: "", email: "" },
    { role: "family_principal", name: "", email: "" },
  ]);

  const roleLabels: Record<string, string> = {
    fund_admin: "Fund Administrator",
    banking_rm: "Banking Relationship Manager",
    fo_rm: "Family Office Relationship Manager",
    family_principal: "Family Principal",
  };

  const addParty = () => {
    setParties([...parties, { role: "other", name: "", email: "" }]);
  };

  const removeParty = (index: number) => {
    setParties(parties.filter((_, i) => i !== index));
  };

  const updateParty = (index: number, field: keyof Party, value: string) => {
    const updated = [...parties];
    updated[index] = { ...updated[index], [field]: value };
    setParties(updated);
  };

  const handleDeploy = async () => {
    if (!name || !emailAlias) {
      toast({
        title: "Missing Information",
        description: "Please provide agent name and email alias",
        variant: "destructive",
      });
      return;
    }

    const validParties = parties.filter(p => p.name && p.email);
    if (validParties.length === 0) {
      toast({
        title: "Missing Parties",
        description: "Please add at least one party contact",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (!orgId) {
        toast({
          title: "Error",
          description: "Organization context not found",
          variant: "destructive",
        });
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      
      const { error } = await supabase.from("workflow_agents").insert({
        org_id: orgId,
        family_id: familyId || null,
        name,
        type: "capital_call",
        email_alias: emailAlias,
        parties: validParties,
        status: "active",
        created_by: userData.user?.id,
      });

      if (error) throw error;

      toast({
        title: "Agent Deployed",
        description: `${name} is now monitoring ${emailAlias}`,
      });

      // Reset form
      setName("");
      setEmailAlias("");
      setParties([
        { role: "fund_admin", name: "", email: "" },
        { role: "banking_rm", name: "", email: "" },
        { role: "fo_rm", name: "", email: "" },
        { role: "family_principal", name: "", email: "" },
      ]);

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Deployment Failed",
        description: error instanceof Error ? error.message : "Deployment failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Deploy Capital Call Agent</DialogTitle>
          <DialogDescription>
            Set up an agent to process capital call statements and coordinate with relevant parties
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Agent Configuration */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Agent Name</Label>
              <Input
                id="agent-name"
                placeholder="e.g., Smith Family Capital Call Agent"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-alias">Email Alias</Label>
              <Input
                id="email-alias"
                type="email"
                placeholder="e.g., family@usevintage.com"
                value={emailAlias}
                onChange={(e) => setEmailAlias(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Capital call statements sent to this email will be automatically processed
              </p>
            </div>
          </div>

          {/* Party Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Relevant Parties</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addParty}
              >
                <Plus className="h-4 w-4" />
                Add Party
              </Button>
            </div>

            <div className="space-y-3">
              {parties.map((party, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      {roleLabels[party.role] || "Other Party"}
                    </Label>
                    {index >= 4 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeParty(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {index >= 4 && (
                      <Select
                        value={party.role}
                        onValueChange={(value) => updateParty(index, "role", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fund_admin">Fund Administrator</SelectItem>
                          <SelectItem value="banking_rm">Banking RM</SelectItem>
                          <SelectItem value="fo_rm">Family Office RM</SelectItem>
                          <SelectItem value="family_principal">Family Principal</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    <Input
                      placeholder="Contact Name"
                      value={party.name}
                      onChange={(e) => updateParty(index, "name", e.target.value)}
                      className={index < 4 ? "col-span-1" : ""}
                    />
                    <Input
                      type="email"
                      placeholder="Email Address"
                      value={party.email}
                      onChange={(e) => updateParty(index, "email", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeploy} disabled={isSubmitting}>
            {isSubmitting ? "Deploying..." : "Deploy Agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
